import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import type { AuthSessionDto } from '@/lib/connect-api';
import type { JourneyConfiguration } from '@/types/domain/configuration';

/**
 * Writes the Stage-1 session for a validated access code, then persists the auth mode.
 *
 * The config fetch authenticates by reading the access code back out of localStorage,
 * so the code has to be written before it — which is why the two live together rather
 * than at each call site. Anything else authenticating as `limited` needs the same.
 *
 * Announces the writes on return: same-tab changes don't fire `storage`, so the auth
 * context would otherwise keep reporting the visitor as signed out until a reload.
 */
export async function persistLimitedSession(
  accessCode: string,
  session: AuthSessionDto,
  loadAccountConfig: (accountId: string) => Promise<JourneyConfiguration>,
): Promise<void> {
  localStorage.setItem(STORAGE_KEYS.ACCESS_CODE, accessCode);
  localStorage.setItem(STORAGE_KEYS.AUTH_STAGE, 'limited');
  localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, session.account_id);

  // Cleared rather than left when the new session has no name: switching accounts
  // without signing out would otherwise show the previous customer's name.
  const customerName = [session.customer_given_name, session.customer_family_name].filter(Boolean).join(' ');
  if (customerName) {
    localStorage.setItem(STORAGE_KEYS.CUSTOMER_NAME, customerName);
  } else {
    localStorage.removeItem(STORAGE_KEYS.CUSTOMER_NAME);
  }

  const accountConfig = await loadAccountConfig(session.account_id);
  localStorage.setItem(STORAGE_KEYS.AUTH_MODE, accountConfig.authentication?.authMode || 'none');

  window.dispatchEvent(new Event('localStorageAuthUpdate'));
}
